import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import {
  dismissLocationTooltip,
  reopenLocationTooltip,
  updatePosition,
} from '../../redux/locationSlice'
import {
  createDraggablePin,
  createSelectedPin,
  createTooltipOverlay,
} from './createPinMarker'

const PinMarkers = ({
  googleMap,
  getGoogleMaps,
  selectedLocationLat,
  selectedLocationLng,
  isEditing,
  isAdding,
  isDesktop,
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const position = useSelector((state) => state.location.position)
  const tooltipOpen = useSelector((state) => state.location.tooltipOpen)

  const [draggablePosition, setDraggablePosition] = useState(
    isDesktop ? position : null,
  )

  useEffect(() => {
    setDraggablePosition(isDesktop ? position : null)
  }, [position, isDesktop])

  const selectedPinRef = useRef(null)
  const draggablePinRef = useRef(null)
  const tooltipOverlayRef = useRef(null)

  const tooltipOpenRef = useRef(tooltipOpen)
  useEffect(() => {
    tooltipOpenRef.current = tooltipOpen
  }, [tooltipOpen])

  useEffect(() => {
    if (!googleMap || !getGoogleMaps) {
      return
    }

    const google = getGoogleMaps()

    const hasSelectedLocation =
      selectedLocationLat != null && selectedLocationLng != null

    if (!hasSelectedLocation) {
      if (selectedPinRef.current) {
        selectedPinRef.current.setMap(null)
        selectedPinRef.current = null
      }
      return
    }

    if (!selectedPinRef.current) {
      selectedPinRef.current = createSelectedPin(
        google,
        googleMap,
        { lat: selectedLocationLat, lng: selectedLocationLng },
        { isEditing },
      )
    } else {
      selectedPinRef.current.updatePosition(
        selectedLocationLat,
        selectedLocationLng,
      )
      selectedPinRef.current.updateEditingState(isEditing)

      if (selectedPinRef.current.getMap() !== googleMap) {
        selectedPinRef.current.setMap(googleMap)
      }
    }

    return () => {
      if (selectedPinRef.current) {
        selectedPinRef.current.setMap(null)
        selectedPinRef.current = null
      }
    }
  }, [
    googleMap,
    getGoogleMaps,
    selectedLocationLat,
    selectedLocationLng,
    isEditing,
  ])

  useEffect(() => {
    if (!googleMap || !getGoogleMaps) {
      return
    }

    const showDraggable =
      isDesktop && (isEditing || isAdding) && draggablePosition

    if (!showDraggable) {
      if (draggablePinRef.current) {
        draggablePinRef.current.destroy()
        draggablePinRef.current = null
      }
      if (tooltipOverlayRef.current) {
        tooltipOverlayRef.current.setMap(null)
        tooltipOverlayRef.current = null
      }
      return
    }

    const google = getGoogleMaps()

    if (!draggablePinRef.current) {
      draggablePinRef.current = createDraggablePin(
        google,
        googleMap,
        { lat: draggablePosition.lat, lng: draggablePosition.lng },
        {
          isAdding,
          onDragEnd: (newPosition) => {
            dispatch(updatePosition(newPosition))
          },
        },
      )

      draggablePinRef.current._dragListener = google.event.addListener(
        draggablePinRef.current,
        'drag',
        (e) => {
          if (tooltipOverlayRef.current) {
            tooltipOverlayRef.current.updatePosition(
              e.latLng.lat(),
              e.latLng.lng(),
            )
          }
        },
      )

      google.event.addListener(draggablePinRef.current, 'dragend', (e) => {
        if (tooltipOverlayRef.current) {
          tooltipOverlayRef.current.updatePosition(
            e.latLng.lat(),
            e.latLng.lng(),
          )
        }
      })

      google.event.addListener(draggablePinRef.current, 'click', () => {
        dispatch(reopenLocationTooltip())
      })

      if (tooltipOpenRef.current) {
        if (tooltipOverlayRef.current) {
          tooltipOverlayRef.current.setMap(null)
          tooltipOverlayRef.current = null
        }
        tooltipOverlayRef.current = createTooltipOverlay(
          google,
          googleMap,
          { lat: draggablePosition.lat, lng: draggablePosition.lng },
          t('locations.index.editmarker_html'),
          () => dispatch(dismissLocationTooltip()),
        )
      }
    } else {
      const current = draggablePinRef.current.getPosition()
      const latChanged = Math.abs(current.lat() - draggablePosition.lat) > 1e-9
      const lngChanged = Math.abs(current.lng() - draggablePosition.lng) > 1e-9
      if (latChanged || lngChanged) {
        draggablePinRef.current.updatePosition(
          draggablePosition.lat,
          draggablePosition.lng,
        )
        if (tooltipOverlayRef.current) {
          tooltipOverlayRef.current.updatePosition(
            draggablePosition.lat,
            draggablePosition.lng,
          )
        }
      }

      if (draggablePinRef.current.getMap() !== googleMap) {
        draggablePinRef.current.setMap(googleMap)
      }
    }

    return () => {
      if (draggablePinRef.current) {
        draggablePinRef.current.destroy()
        draggablePinRef.current = null
      }
      if (tooltipOverlayRef.current) {
        tooltipOverlayRef.current.setMap(null)
        tooltipOverlayRef.current = null
      }
    }
  }, [
    googleMap,
    getGoogleMaps,
    isDesktop,
    isEditing,
    isAdding,
    draggablePosition,
    dispatch,
    t,
  ])

  useEffect(() => {
    if (!googleMap || !getGoogleMaps || !draggablePinRef.current) {
      if (tooltipOverlayRef.current) {
        tooltipOverlayRef.current.setMap(null)
        tooltipOverlayRef.current = null
      }
      return
    }

    const google = getGoogleMaps()
    const pinPosition = draggablePinRef.current.getPosition()
    const pos = {
      lat: pinPosition.lat(),
      lng: pinPosition.lng(),
    }

    if (tooltipOpen) {
      if (!tooltipOverlayRef.current) {
        tooltipOverlayRef.current = createTooltipOverlay(
          google,
          googleMap,
          pos,
          t('locations.index.editmarker_html'),
          () => dispatch(dismissLocationTooltip()),
        )
      }
    } else {
      if (tooltipOverlayRef.current) {
        tooltipOverlayRef.current.setMap(null)
        tooltipOverlayRef.current = null
      }
    }
  }, [tooltipOpen, googleMap, getGoogleMaps, t, dispatch])

  return null
}

export default PinMarkers
